import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Users, TrendingUp, Phone, Mail, Activity } from 'lucide-react';
import { toast } from 'sonner';
import IntegrationSetupModal from '@/components/IntegrationSetupModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState({
    total_leads: 0,
    hot_leads: 0,
    warm_leads: 0,
    cold_leads: 0,
    agents_count: 0,
    workflows_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [integrationStatus, setIntegrationStatus] = useState({
    fub_connected: false,
    retell_connected: false,
    onboarding_completed: false
  });
  const [showIntegrationModal, setShowIntegrationModal] = useState(null);
  const [lastSaved, setLastSaved] = useState(new Date());

  useEffect(() => {
    fetchStats();
    fetchIntegrationStatus();
    
    // Auto-save every 5 minutes
    const autoSaveInterval = setInterval(() => {
      handleAutoSave();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(autoSaveInterval);
  }, []);

  const handleAutoSave = async () => {
    try {
      // Auto-save any unsaved data
      // For now, just refresh stats
      await fetchStats();
      await fetchIntegrationStatus();
      setLastSaved(new Date());
      console.log('Auto-save completed at:', new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchIntegrationStatus = async () => {
    try {
      const response = await axios.get(`${API}/user/integrations/status`, {
        headers: getAuthHeader()
      });
      setIntegrationStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch integration status');
    }
  };

  const statCards = [
    {
      title: 'Total Leads',
      value: stats.total_leads,
      icon: Users,
      color: 'from-blue-400 to-blue-600',
      testId: 'total-leads-stat',
      onClick: () => navigate('/dashboard/leads')
    },
    {
      title: 'Hot Leads',
      value: stats.hot_leads,
      icon: TrendingUp,
      color: 'from-red-400 to-red-600',
      testId: 'hot-leads-stat',
      onClick: () => navigate('/dashboard/leads?filter=hot')
    },
    {
      title: 'Warm Leads',
      value: stats.warm_leads,
      icon: Activity,
      color: 'from-orange-400 to-orange-600',
      testId: 'warm-leads-stat',
      onClick: () => navigate('/dashboard/leads?filter=warm')
    },
    {
      title: 'Cold Leads',
      value: stats.cold_leads,
      icon: Phone,
      color: 'from-cyan-400 to-cyan-600',
      testId: 'cold-leads-stat',
      onClick: () => navigate('/dashboard/leads?filter=cold')
    },
    {
      title: 'Active Agents',
      value: stats.agents_count,
      icon: Mail,
      color: 'from-purple-400 to-purple-600',
      testId: 'agents-stat',
      onClick: () => navigate('/dashboard/agents')
    },
    {
      title: 'Workflows',
      value: stats.workflows_count,
      icon: Activity,
      color: 'from-green-400 to-green-600',
      testId: 'workflows-stat',
      onClick: () => navigate('/dashboard/workflows')
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-gray-700 font-medium">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400">Welcome to your AI Lead Qualification Platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card
              key={index}
              data-testid={stat.testId}
              onClick={stat.onClick}
              className="p-6 card-hover bg-white border-0 shadow-lg cursor-pointer hover:shadow-xl transition-all"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div
                  className={`w-14 h-14 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-0 shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button 
              onClick={() => navigate('/dashboard/leads')}
              className="w-full text-left px-4 py-3 bg-white rounded-lg shadow hover:shadow-md transition-all cursor-pointer"
            >
              <div className="font-semibold text-gray-900">Create New Lead</div>
              <div className="text-sm text-gray-600">Add a new prospect to your pipeline</div>
            </button>
            <button 
              onClick={() => navigate('/dashboard/agents')}
              className="w-full text-left px-4 py-3 bg-white rounded-lg shadow hover:shadow-md transition-all cursor-pointer"
            >
              <div className="font-semibold text-gray-900">Configure Voice Agent</div>
              <div className="text-sm text-gray-600">Set up AI-powered phone calls</div>
            </button>
            <button 
              onClick={() => navigate('/dashboard/workflows')}
              className="w-full text-left px-4 py-3 bg-white rounded-lg shadow hover:shadow-md transition-all cursor-pointer"
            >
              <div className="font-semibold text-gray-900">Build Workflow</div>
              <div className="text-sm text-gray-600">Create conversation flows</div>
            </button>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-0 shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Integration Status</h3>
          <div className="space-y-3">
            {/* Follow Up Boss */}
            <button
              onClick={() => !integrationStatus.fub_connected && setShowIntegrationModal('fub')}
              className={`w-full flex items-center justify-between p-3 bg-white rounded-lg transition-all ${
                !integrationStatus.fub_connected ? 'hover:shadow-md cursor-pointer' : ''
              }`}
            >
              <span className="font-medium text-gray-900">Follow Up Boss CRM</span>
              {integrationStatus.fub_connected ? (
                <a
                  href="https://aipropflo.followupboss.com/2/people"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium hover:bg-green-200 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  Connected ✓
                </a>
              ) : (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium hover:bg-yellow-200 transition-colors">
                  Click to Configure →
                </span>
              )}
            </button>

            {/* Retell AI */}
            <button
              onClick={() => !integrationStatus.retell_connected && setShowIntegrationModal('retell')}
              className={`w-full flex items-center justify-between p-3 bg-white rounded-lg transition-all ${
                !integrationStatus.retell_connected ? 'hover:shadow-md cursor-pointer' : ''
              }`}
            >
              <span className="font-medium text-gray-900">Retell AI Voice</span>
              {integrationStatus.retell_connected ? (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  Connected ✓
                </span>
              ) : (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium hover:bg-yellow-200 transition-colors">
                  Click to Configure →
                </span>
              )}
            </button>

            {/* OpenAI - Always Active */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg">
              <span className="font-medium text-gray-900">OpenAI GPT-4o</span>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                Active ✓
              </span>
            </div>

            {/* Twilio - Always Active */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg">
              <span className="font-medium text-gray-900">Twilio SMS/Voice</span>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                Active ✓
              </span>
            </div>

            {/* Make.com - Always Active */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg">
              <span className="font-medium text-gray-900">Make.com Automation</span>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                Active ✓
              </span>
            </div>
          </div>
          
          {integrationStatus.fub_connected && (
            <div className="mt-4">
              <a
                href="https://aipropflo.followupboss.com/2/people"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Open Follow Up Boss Dashboard →
              </a>
            </div>
          )}
          
          {/* Auto-save indicator */}
          <div className="mt-4 text-xs text-gray-500 text-center">
            💾 Auto-saves every 5 minutes • Last saved: {lastSaved.toLocaleTimeString()}
          </div>
        </Card>
      </div>

      {/* Integration Setup Modal */}
      {showIntegrationModal && (
        <IntegrationSetupModal
          integration={showIntegrationModal}
          onClose={() => setShowIntegrationModal(null)}
          onSuccess={() => {
            setShowIntegrationModal(null);
            fetchIntegrationStatus();
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;